import { useSyncExternalStore } from "react";
import { supabase } from "./supabase";

const KEY = "dropit-last-user";
let state = { expired: false, refreshing: null };
const subs = new Set();
const emit = () => subs.forEach((f) => f());

export const lastKnownUser = () => { try { return JSON.parse(localStorage.getItem(KEY) || "null"); } catch { return null; } };
export const rememberUser = (u) => { if (u) localStorage.setItem(KEY, JSON.stringify({ id: u.id, email: u.email, name: u.user_metadata?.full_name || null })); };
export const forgetUser = () => localStorage.removeItem(KEY);

export const setSessionExpired = (v) => { if (state.expired !== v) { state = { ...state, expired: v }; emit(); } };
export const useSessionExpired = () => useSyncExternalStore((f) => { subs.add(f); return () => subs.delete(f); }, () => state.expired, () => false);

export const isAuthError = (e) => {
  const msg = `${e?.message || ""} ${e?.code || ""} ${e?.error_code || ""}`;
  return e?.status === 401 || /JWT|jwt expired|invalid claim|refresh_token|PGRST301|not authenticated|session_not_found|invalid.*token/i.test(msg);
};
const isInvalidRefresh = (e) => /refresh_token_not_found|invalid refresh token|refresh token.*(not found|invalid|revoked|already used)|session_not_found/i.test(e?.message || "");

const isFresh = (s) => s?.expires_at && s.expires_at * 1000 - Date.now() > 60_000;

// Returns { session, offline, expired }. Never throws. Only marks expired on a definitive refresh failure while online.
export const ensureSession = async () => {
  const { data: { session } } = await supabase.auth.getSession();
  if (isFresh(session)) { if (state.expired) setSessionExpired(false); return { session }; }
  if (!session && !lastKnownUser()) return { session: null, expired: false };
  if (!navigator.onLine) return { session, offline: true };
  if (!state.refreshing) state.refreshing = supabase.auth.refreshSession().finally(() => { state.refreshing = null; });
  const { data, error } = await state.refreshing;
  if (data?.session) { setSessionExpired(false); return { session: data.session }; }
  if (error && !isInvalidRefresh(error) && !/400|401|403/.test(String(error.status || ""))) return { session, offline: true };
  setSessionExpired(true);
  return { session: null, expired: true };
};

// Run a Supabase/API call; on auth error refresh once and retry (online), or report offline.
export const withAuthRetry = async (fn) => {
  const first = await fn();
  const err = first?.error || (first instanceof Error ? first : null);
  if (!err || !isAuthError(err)) return first;
  const r = await ensureSession();
  if (r.session) return fn();
  return first;
};
